import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { retrieveWorksheetAPI, createAssignmentAPI } from "./worksheetAPI";
import { getRandom5Sentences } from "../../utils/worksheetUtils";

// 리더스북에 딸린 문법책 · 단어장(지인에듀 7판 kind). api assignment/readers_book.COMPANION_BOOK_KIND_LIST 와 같은 값
const COMPANION_BOOK_KIND_LIST = ["grammar_book", "vocab_book"];

// 지인에듀 단어 3판(2026-11)의 짝 표현 · 문맥 문제. [유형, 문제 문구]. 차례는 지인에듀 문제지와 같다.
// 서버 utils/assignment/vocabulary_section_builder.py 의 같은 이름 목록과 짝이다(문구 한 글자도 같아야 한다)
const VOCABULARY_TEXT_CHOICE_TYPE_LIST = [
  ["collocation_blank_choice", "주어진 한국어 뜻을 참고하여 짝 표현의 빈칸에 들어갈 가장 알맞은 말을 고르시오."],
  ["collocation_odd_choice", "주어진 단어와 함께 쓰는 표현으로 어색한 것을 고르시오."],
  ["collocation_meaning_choice", "주어진 짝 표현의 한국어 뜻으로 알맞은 것을 고르시오."],
  ["context_blank_choice", "주어진 한국어 해석을 참고하여 문장의 빈칸에 들어갈 가장 알맞은 영단어를 고르시오."],
];
const VOCABULARY_TEXT_WRITE_TYPE_LIST = [
  ["collocation_blank", "주어진 한국어 뜻을 참고하여 짝 표현의 빈칸에 들어갈 말을 적으시오."],
  ["collocation_write", "주어진 한국어 뜻에 해당하는 짝 표현을 영어로 적으시오."],
];

// 지인에듀는 삽화를 (document_idx, paragraph_idx) 별 다운로드 주소 목록(illustrations)으로 내려준다.
// 이미지를 실제로 받아 오는 것은 서버 몫이다. 지인에듀 OAuth 토큰은 서버에만 있고, 학생은 지인에듀
// 계정이 없어 이 주소를 직접 열 수 없다. 여기서는 지문마다 위치와 주소만 붙여 출제 payload 로 넘긴다.
const findIllustration = (illustrations, documentIdx, paragraphIdx) =>
  (illustrations || []).find(
    (illustration) =>
      illustration.document_idx === documentIdx &&
      illustration.paragraph_idx === paragraphIdx
  ) || null;

// 지인에듀는 지문 낭독 음원을 (document_idx, paragraph_idx) 별 목록(audios)으로 준다. 재생 주소는 url + ".mp3".
const findPassageAudioUrl = (audios, documentIdx, paragraphIdx) => {
  const audio = (audios || []).find(
    (one) => one.document_idx === documentIdx && one.paragraph_idx === paragraphIdx
  );
  return audio && audio.url ? `${audio.url}.mp3` : "";
};

const initialState = {
  worksheet: { title: "", data: [] }, // 단일 Worksheet 데이터
  loading: false, // 로딩 상태
  error: null, // 에러 상태
};

export const retrieveWorksheet = createAsyncThunk(
  "worksheet/retrieveWorksheet",
  async ({ id, type, author_name }, { rejectWithValue }) => {
    try {
      const response = await retrieveWorksheetAPI(id, type);

      // 지인에듀 문법뱅크(2026-10-24): 개념 학습지 + 문제. 문항(documents[].data)과 '책 보기'로 넘겨 읽을 쪽 번호를 싣는다.
      // 서버(api assignment/ginedu_grammar_bank.py)가 문항을 문제뱅크 문법 모양으로 옮기고 쪽 그림을 받는다.
      if (type === "grammar_bank") {
        return {
          id: id,
          title: response.title,
          type: type,
          author_name: author_name,
          data: {
            documents: (response.data && response.data.documents) || [],
            pages: (response.pages || []).filter((page) => page.document_idx === 0).map((page) => page.page),
          },
        };
      }

      if (type === "analysis") {
        const title = response.title;
        const analysisDocs = response.data.documents.filter(
          (doc) => doc.type === "analysis"
        )[0];
        const examDocs = response.data.online_documents.filter(
          (doc) => doc.type === "analysis:exam"
        )[0];

        const processedParagraphs = [];
        // '책 보기'(2026-10-24)로 넘겨 읽을 지문분석 쪽 번호. 분석지 한 벌(document_idx 0)의 쪽이다.
        const analysisPageList = (response.pages || [])
          .filter((page) => page.document_idx === 0)
          .map((page) => page.page);

        for (let idx = 0; idx < analysisDocs.paragraph_data.length; idx++) {
          const para = analysisDocs.paragraph_data[idx];
          const exam = examDocs.paragraph_data[idx].exams;
          const sectionDataList = [];
          // 책 한 벌이라 첫 지문에만 둔다(서버가 맨 앞 섹션으로 만든다)
          if (idx === 0 && analysisPageList.length) {
            sectionDataList.push({ type: "ebook", data: { pages: analysisPageList } });
          }
          const keypoints = para.keypoints || null;
          const words = para.words || [];
          const syntax_analysis = para.syntax_analysis || [];
          const sentences = para.sentence_eng_kor || [];
          // 끊어읽기 판본. 같은 문장을 영어와 우리말 모두 " / " 로 끊어서 준다.
          // 없는 시험지도 있을 수 있어 통문장으로 물러난다.
          const chunkedSentences =
            para.sentence_translation_data ||
            para.sentence_translation_data_direct ||
            sentences;

          const korBlank = exam
            .filter((item) => item.type === "analysis:exam:kor_blank")
            .map((item) => item.data);
          const engBlank = exam
            .filter((item) => item.type === "analysis:exam:eng_blank")
            .map((item) => item.data);
          const engBlankRandom = exam
            .filter((item) => item.type === "analysis:exam:eng_blank_random")
            .map((item) => item.data);
          const engScramble = exam
            .filter((item) => item.type === "analysis:exam:eng_scramble")
            .map((item) => item.data);

          sectionDataList.push({
            type: "keypoints",
            data: keypoints,
          });
          sectionDataList.push({
            type: "reading",
            data: sentences,
            // 지인에듀가 지문마다 붙여 주는 강의(유튜브·드라이브). 학생 지문읽기 화면이 띄운다.
            video_url: para.video_url || "",
          });
          sectionDataList.push({
            type: "direct_translation",
            data: chunkedSentences,
            words: words,
          });
          sectionDataList.push({
            type: "words",
            data: words,
          });
          sectionDataList.push({
            type: "syntax_analysis",
            data: syntax_analysis,
          });
          sectionDataList.push({
            type: "flow",
            data: getRandom5Sentences(sentences),
          });
          // 지인에듀 문법뱅크 문항. 서버가 학생 어법 화면 모양으로 옮기고, 없으면 안 만든다.
          sectionDataList.push({
            type: "grammar",
            data: para.grammar_items || [],
          });
          sectionDataList.push({
            type: "sentence_test",
            data: [
              { type: "kor_blank", data: korBlank },
              { type: "eng_blank", data: engBlank },
              { type: "eng_blank_random", data: engBlankRandom },
              { type: "eng_scramble", data: engScramble },
            ],
          });

          const newItem = {
            title: sentences[0][0].split(" ").slice(0, 6).join(" ") + "...",
            // 지문분석 삽화는 분석지 한 벌에만 있으므로 document_idx 는 늘 0 이다.
            illustration: findIllustration(response.illustrations, 0, idx),
            data: sectionDataList,
          };

          processedParagraphs.push(newItem);
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: processedParagraphs,
        };
      } else if (type === "comprehensive" && response.chapter_book) {
        // 챕터북(원서 모드의 한 갈래, 2026-10-19). 챕터 하나가 지문 하나다. 본문 · 그림 · 영상 · 문제는
        // 서버가 지인에듀에서 직접 받으므로(api/assignment/chapter_book.py) 여기서는 고른 챕터 순번만 싣는다.
        const chapterList = response.chapter_book.chapters || [];
        const chapterParagraphList = chapterList.map((chapter, index) => {
          const chapterTitle = chapter.title || `Chapter ${index + 1}`;
          return {
            // 서버 chapter_book.chapter_title_of 와 같은 꼴(챕터가 하나면 책 제목)
            title: (chapterList.length === 1 ? response.title || chapterTitle : `${index + 1}. ${chapterTitle}`).slice(0, 100),
            full_text: chapter.text || "",
            full_text_korean: "",
            illustration: null,
            data: [{ type: "chapter_book", chapter_index: chapter.idx ?? index }],
          };
        });

        return {
          id: id,
          title: response.title,
          type: type,
          mode: "chapter_book",
          author_name: author_name,
          setLabelList: ["챕터북"],
          data: [chapterParagraphList],
        };
      } else if (type === "comprehensive" && response.mode === "book") {
        // 원서 워크북(지인에듀 mode=book, 2026-10-16). 지문 묶음 대신 "장 x 세트" 목록(chapters)이 온다.
        // 세트 하나 = 시험지 하나라서 chapters 한 줄을 세트 하나로 보고 통째로 출제한다(고르는 활동 없음).
        // 학생 화면은 student 의 BookWorkbookPage 가 이 모양을 그대로 읽는다.
        const bookData = response.data || {};
        // 표지(문제집 첫 쪽)를 받으려고 세트가 어느 문서의 것인지 같이 적어 둔다
        const chapterList = (bookData.documents || []).flatMap((document, documentIndex) =>
          (document.chapters || []).map((chapter) => ({ ...chapter, document_idx: documentIndex }))
        );
        const setLabelList = [];
        const processedSetList = chapterList.map((chapter, chapterIndex) => {
          const setLabel = `${chapter.chapter_label || `${chapterIndex + 1}장`} · ${
            (chapter.set_index || 0) + 1
          }세트`;
          setLabelList.push(setLabel);
          // 그림 선 긋기 그림. 받아 오는 것은 서버가 위치(document_idx, paragraph_idx)로 다시 만든다.
          const pictureList = (chapter.pictures || []).map((picture) => {
            const illustration =
              findIllustration(response.illustrations, chapterIndex, picture.picture_idx) ||
              (response.illustrations || []).find(
                (one) => one.kind === "book_picture" && one.word === picture.word
              ) ||
              null;
            return {
              ...picture,
              document_idx: illustration ? illustration.document_idx : null,
              paragraph_idx: illustration ? illustration.paragraph_idx : null,
            };
          });
          return [
            {
              // 서버 Paragraph.title 이 100자까지라 자른다.
              title: setLabel.slice(0, 100),
              full_text: chapter.chapter_text || "",
              full_text_korean: "",
              illustration: null,
              data: [
                {
                  type: "book",
                  data: {
                    ...chapter,
                    pictures: pictureList,
                    book_title: response.title,
                    language: bookData.language || "ko",
                    instructions: bookData.instructions || {},
                    extra_types: bookData.extra_types || {},
                    // 지문분석 조교 워크북은 지문마다 구문분석이 붙어 온다(response.book_syntax, 2026-10-24).
                    // 서버가 이것으로 지문마다 구문분석 섹션을 만든다.
                    syntax_passages: ((response.book_syntax || {}).passages || []).filter(
                      (passage) => (passage.chapter_idx ?? 0) === chapterIndex
                    ),
                    // '책 보기'(2026-10-24)로 넘겨 읽을 문제집 쪽 번호. 쪽은 문서 단위라 세트가 하나인 문서에만 싣는다.
                    workbook_pages:
                      ((bookData.documents || [])[chapter.document_idx]?.chapters || []).length === 1
                        ? (response.pages || [])
                            .filter((page) => page.document_idx === chapter.document_idx)
                            .map((page) => page.page)
                        : [],
                  },
                },
              ],
            },
          ];
        });

        return {
          id: id,
          title: response.title,
          type: type,
          mode: "book",
          author_name: author_name,
          setLabelList,
          data: processedSetList,
        };
      } else if (type === "comprehensive") {
        const title = response.title;
        const comprehensiveDocs = response.data.documents.filter(
          (doc) => doc.type === "comprehensive"
        );

        const processedSetList = [];

        for (let idx = 0; idx < comprehensiveDocs.length; idx++) {
          const comprehensiveDoc = comprehensiveDocs[idx];
          // 아래 지문 루프가 같은 이름(idx)을 다시 쓰므로 시험지 세트 번호를 여기서 잡아 둔다.
          const documentIdx = idx;
          const processedParagraphs = [];

          for (
            let idx = 0;
            idx < comprehensiveDoc.paragraph_data.length;
            idx++
          ) {
            const para = comprehensiveDoc.paragraph_data[idx];
            const full_text = para.paragraph || "";
            const full_text_korean = para.full_text_korean || "";
            const elements = para.elements || [];

            const processedData = [];

            for (let elementType of [
              "scramble",
              "understanding",
              "keyword",
              "translation",
              "ox",
              "summary",
            ]) {
              let elementData = elements.filter((element) =>
                element.type.includes(elementType)
              );
              if (["ox", "summary"].includes(elementType)) {
                elementData = elementData.flatMap((selectedElement) =>
                  selectedElement.problems.map((problem) => {
                    return {
                      ...problem,
                      type: selectedElement.type.split(":")[1],
                    };
                  })
                );
              } else {
                elementData = elementData.flatMap(
                  (element) => element.problems
                );
              }

              if (elementData.length > 0) {
                processedData.push({
                  type: elementType,
                  data: elementData,
                });
              }
            }
            processedParagraphs.push({
              title: full_text.split(" ").slice(0, 6).join(" ") + "...",
              full_text,
              full_text_korean,
              // 내용이해 삽화는 시험지 세트마다 따로 재생성될 수 있어 세트 번호까지 맞춰 찾는다.
              illustration: findIllustration(response.illustrations, documentIdx, idx),
              // 지문 낭독 음원(2026-10-16 지인에듀 audios). url 끝에 .mp3 를 붙이면 로그인 없이 열린다.
              audio_url: findPassageAudioUrl(response.audios, documentIdx, idx),
              data: processedData,
            });
          }
          processedSetList.push(processedParagraphs);
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: processedSetList,
        };
      } else if (type === "paragraph") {
        const title = response.title;
        const paragraphDocs = response.data.documents;

        const processedData = [];

        for (let idx = 0; idx < paragraphDocs.length; idx++) {
          const sectionData = paragraphDocs[idx];
          processedData.push({
            type_name: sectionData.subtitle,
            data: sectionData.data
              .map((selectedElement) => ({
                ...selectedElement,
                type: selectedElement.type.replace(/^paragraph:/, ""),
              })),
          });
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: [{ title: "모의고사", data: processedData }],
        };
      } else if (type === "vocabulary") {
        const title = response.title;
        const wordList = response.data.word_list;
        const examList = response.data.exams;

        const newParagraphList = [];

        const studyDataList = [];
        wordList.forEach((_, index) => {
          if (index % 100 === 0) {
            const start = index + 1;
            const end = Math.min(index + 100, wordList.length);
            const typeName = `단어장 ${String(start).padStart(3, "0")}-${String(
              end
            ).padStart(3, "0")}`;

            studyDataList.push({
              type: "study",
              type_name: typeName,
              data: { words: wordList.slice(index, index + 100) },
            });
          }
        });
        newParagraphList.push({
          title: "[단어장]",
          data: studyDataList,
        });

        const matchingDataList = [];
        wordList.forEach((_, index) => {
          if (index % 100 === 0) {
            const start = index + 1;
            const end = Math.min(index + 100, wordList.length);
            const typeName = `매칭게임 ${String(start).padStart(
              3,
              "0"
            )}-${String(end).padStart(3, "0")}`;

            matchingDataList.push({
              type: "matching",
              type_name: typeName,
              data: { words: wordList.slice(index, index + 100) },
            });
          }
        });
        newParagraphList.push({
          title: "[매칭게임]",
          data: matchingDataList,
        });

        const testDataList = [];
        examList.forEach((examData, examIndex) => {
          const newExamData = [];
          const typeName = "테스트 " + (examIndex + 1);
          const problems = examData.problems;

          // 지인에듀 3판(2026-11)부터 객관식은 보기 글자(choice_texts)를 직접 주고 오답 자리의 단어 번호는 null 이다.
          // 예전처럼 번호로 단어를 찾으면 null 에서 멈춰 출제가 안 됐다. 보기 글자가 있으면 그것을 쓴다
          problems.engkor_choice.forEach((problem) => {
            newExamData.push({
              type: "engkor_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].word,
              question: "주어진 영단어의 한국어 뜻을 고르시오.",
              choices: Array.isArray(problem.choice_texts) ? problem.choice_texts : problem.choices_word_indices.map((wordIndex, index) =>
                wordList[wordIndex].kor
                  .split("\n")
                  [problem.choice_line_indices[index]].replace(/\[.*?\]/g, "")
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.koreng_choice.forEach((problem) => {
            newExamData.push({
              type: "koreng_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].kor
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 한국어 뜻에 해당하는 영단어를 고르시오.",
              choices: Array.isArray(problem.choice_texts) ? problem.choice_texts : problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.syn_choice.forEach((problem) => {
            newExamData.push({
              type: "syn_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].syn
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 동의어를 가진 영단어를 고르시오.",
              choices: Array.isArray(problem.choice_texts) ? problem.choice_texts : problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.ant_choice.forEach((problem) => {
            newExamData.push({
              type: "ant_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].ant
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 반의어를 가진 영단어를 고르시오.",
              choices: Array.isArray(problem.choice_texts) ? problem.choice_texts : problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.engeng_choice.forEach((problem) => {
            newExamData.push({
              type: "engeng_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].eng
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 영영 뜻풀이를 가진 영단어를 고르시오.",
              choices: Array.isArray(problem.choice_texts) ? problem.choice_texts : problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.example_blank_choice.forEach((problem) => {
            newExamData.push({
              type: "example_blank_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].replace(/\{.*?\}/g, (match) =>
                  "_".repeat(match.length)
                ),
              text1:
                wordList[problem.answer_word_idx].example_translation.split(
                  "\n"
                )[problem.question_line_idx],
              question:
                "주어진 한국어 뜻을 참고하여 예문의 빈칸에 들어갈 가장 알맞은 영단어를 고르시오.",
              choices: Array.isArray(problem.choice_texts)
                ? problem.choice_texts
                : problem.choices_word_indices.map(
                    (wordIdx, index) =>
                      wordList[wordIdx].example
                        .split("\n")
                        [problem.choice_line_indices[index]].match(/\{(.*?)\}/)[1]
                  ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          // 짝 표현 · 문맥 객관식(지인에듀 3판, 2026-11). 문제 · 보기 글자를 지인에듀가 다 만들어 준다.
          // 문구는 지인에듀 문제지와 같다. 서버 utils/assignment/vocabulary_section_builder.py 와 같은 규칙이다
          VOCABULARY_TEXT_CHOICE_TYPE_LIST.forEach(([problemType, questionText]) => {
            (problems[problemType] || []).forEach((problem) => {
              newExamData.push({
                type: problemType,
                answer: problem.answer_choice_idx + 1,
                text: problem.question_text,
                ...(problem.translation ? { text1: problem.translation } : {}),
                question: questionText,
                choices: problem.choice_texts,
                word: wordList[problem.answer_word_idx].word,
              });
            });
          });

          problems.engkor.forEach((problem) => {
            newExamData.push({
              type: "engkor",
              answer: wordList[problem.answer_word_idx].kor.replace("\n", " "),
              text: wordList[problem.answer_word_idx].word,
              question: "주어진 영단어의 한국어 뜻을 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.koreng.forEach((problem) => {
            newExamData.push({
              type: "koreng",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].kor
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 한국어 뜻에 해당하는 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.syn.forEach((problem) => {
            newExamData.push({
              type: "syn",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].syn
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 동의어를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.ant.forEach((problem) => {
            newExamData.push({
              type: "ant",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].ant
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 반의어를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.engeng.forEach((problem) => {
            newExamData.push({
              type: "engeng",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].eng
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 영영 뜻풀이를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.example_blank.forEach((problem) => {
            newExamData.push({
              type: "example_blank",
              answer: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].match(/\{(.*?)\}/)[1],
              text: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].replace(/\{.*?\}/g, (match) =>
                  "_".repeat(match.length)
                ),
              text1:
                wordList[problem.answer_word_idx].example_translation.split(
                  "\n"
                )[problem.question_line_idx],
              question:
                "주어진 한국어 뜻을 참고하여 예문의 빈칸에 들어갈 가장 알맞은 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          // 짝 표현 주관식(지인에듀 3판). 빈칸 채우기는 뜻을 보고 빈칸 말을, 쓰기는 뜻을 보고 표현 전체를 적는다
          VOCABULARY_TEXT_WRITE_TYPE_LIST.forEach(([problemType, questionText]) => {
            (problems[problemType] || []).forEach((problem) => {
              newExamData.push({
                type: problemType,
                answer: problem.answer_text,
                text: problem.question_text,
                ...(problem.translation ? { text1: problem.translation } : {}),
                question: questionText,
                word: wordList[problem.answer_word_idx].word,
              });
            });
          });
          testDataList.push({
            type: "exam",
            type_name: typeName,
            data: newExamData,
          });
        });
        newParagraphList.push({
          title: "[단어 테스트]",
          data: testDataList,
        });

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: newParagraphList,
        };
      } else if (type === "sentence") {
        const title = response.title;
        // For sentence type, keep the original structure with documents and sentence_list
        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: response.data, // Keep the full data object with documents and sentence_list
        };
      } else if (type === "readers_book") {
        // 리더스북(지인에듀, 2026-10-17). '단원 + 문제' 단위인 파트를 골라 낸다(파트 하나가 단원 여러 개에
        // 걸칠 수 있다). 쪽 그림과 음원은 출제할 때 서버가 지인에듀에서 직접 받으므로
        // (api/assignment/readers_book.py) 여기서는 파트 번호와 목록에 보일 글만 싣는다.
        const bookData = response.data || {};
        const unitTitleOf = Object.fromEntries((bookData.units || []).map((unit) => [unit.number, unit.title]));
        // 파닉스북은 파트가 없다. 책 전체를 파트 하나(1번)로 낸다(readers_book.part_list_of).
        const isPhonicsBook =
          response.kind === "phonics_book" ||
          (!(bookData.parts || []).length && (bookData.pages || []).some((page) => page.kind === "phonics"));
        if (isPhonicsBook) {
          const bookTitle = (bookData.book && bookData.book.title) || response.title || "";
          return {
            id: id,
            title: response.title,
            type: type,
            author_name: author_name,
            // 출제 화면이 단계(입모양 · 이야기 듣기 · 책 · 문제)를 고르게 한다(WorksheetModal 의 PHONICS_STEP_LIST)
            is_phonics_book: true,
            data: [{
              title: bookTitle.slice(0, 100),
              part_number: 1,
              full_text: `${(bookData.book && bookData.book.series) || ""}\n${bookTitle}\n\n${(bookData.pages || []).length}쪽`,
              data: [{ type: "ebook" }],
            }],
          };
        }
        // 리더스북에 딸린 문법책 · 단어장(지인에듀 7판)도 파트가 없다. 쪽 그림 위 칸을 푸는 책 한 권을 그대로 낸다
        const companionKind = (bookData.book && bookData.book.companion_kind) || "";
        if (COMPANION_BOOK_KIND_LIST.includes(response.kind) || COMPANION_BOOK_KIND_LIST.includes(companionKind)) {
          const book = bookData.book || {};
          return {
            id: id,
            title: response.title,
            type: type,
            author_name: author_name,
            is_companion_book: true,
            data: [{
              title: `${book.label || ""} · ${book.reader_title || response.title || ""}`.slice(0, 100),
              part_number: 1,
              full_text: `ECHO ${book.reader_book || ""} ${book.label || ""}\n${book.reader_title || ""}\n\n${
                (bookData.pages || []).filter((page) => page.kind !== "answer_key").length
              }쪽`,
              data: [{ type: "ebook" }],
            }],
          };
        }
        return {
          id: id,
          title: response.title,
          type: type,
          author_name: author_name,
          // 출제 화면이 단원을 골라 내게 한다(ParagraphSelectionPage 의 '출제할 것')
          unit_list: (bookData.units || []).map((unit) => ({ number: unit.number, title: unit.title || "" })),
          data: (bookData.parts || []).map((part) => ({
            // 서버 Paragraph.title 과 같은 꼴(readers_book.part_title_of)
            title: `Part ${part.number} · ${part.title || ""}`.slice(0, 100),
            part_number: part.number,
            full_text: [
              ...(part.units || []).map((number) => `Unit ${number} · ${unitTitleOf[number] || ""}`),
              "",
              (part.activities || []).map((activity) => activity.label).join(" · "),
              "",
              part.line || "",
            ].join("\n"),
            data: [{ type: "ebook" }],
          })),
        };
      } else if (type === "grammar") {
        const title = response.title;
        // For grammar type, keep the original structure with documents
        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: response.data, // Keep the full data object with documents
        };
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Unknown error");
    }
  }
);

export const createAssignment = createAsyncThunk(
  "worksheet/createAssignment",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await createAssignmentAPI(data);
      return response;
    } catch (error) {
      // 서버가 내려준 한국어 안내를 그대로 쓴다. 없으면 axios 기본 메시지.
      return rejectWithValue(
        error?.response?.data?.error || error?.message || "Unknown error"
      );
    }
  }
);

const worksheetSlice = createSlice({
  name: "worksheet",
  initialState,
  reducers: {
    clearWorksheet(state) {
      state.worksheet = { title: "", data: [] }; // 선택된 Worksheet 데이터 초기화
      state.error = null; // 에러 초기화
    },
    setWorksheetTitle(state, action) {
      state.worksheet.title = action.payload;
    },
    // 지인에듀에서 받지 않고 화면이 직접 만든 문제지(공식 교재 '발음기호' 과제, OfficialBookPage)
    setWorksheet(state, action) {
      state.worksheet = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(retrieveWorksheet.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.worksheet = { title: "", data: [] }; // 로딩 시작 시 기존 데이터 초기화
      })
      .addCase(retrieveWorksheet.fulfilled, (state, action) => {
        state.loading = false;
        state.worksheet = action.payload; // API로 받은 Worksheet 데이터 저장
      })
      .addCase(retrieveWorksheet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve worksheet";
      })
      .addCase(createAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create assignment";
      });
  },
});

export const { clearWorksheet, setWorksheetTitle, setWorksheet } = worksheetSlice.actions;
export default worksheetSlice.reducer;
